<template>
  <div class="element-based-home">
    <EbIcons />

    <section class="page-wrap">
      <h1 class="page-title">要素式文书</h1>
      <div class="page-subtitle">
        输入案件情况或上传相关材料，AI
        将提取案件要素，并通过对话补充必要信息后生成要素式文书。
      </div>

      <!-- 输入 + 上传区 -->
      <div
        class="card composer"
        :class="{ dragover: composerDragOver }"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <div v-if="files.length" class="composer-files">
          <div v-for="(f, i) in files" :key="f.name" class="file-chip">
            <div class="file-icon">{{ isPdf(f.name) ? "PDF" : "W" }}</div>
            <div class="file-main">
              <div class="file-name">{{ f.name }}</div>
              <div class="file-meta">
                {{ f.label }} · {{ f.size }} · {{ fileStateText(f) }}
              </div>
            </div>
            <span class="file-badge">{{ f.label }}</span>
            <button type="button" class="icon-btn" @click="removeFile(i)">
              <svg class="icon sm"><use href="#eb-x" /></svg>
            </button>
          </div>
        </div>

        <textarea
          v-model="caseText"
          class="composer-textarea"
          :placeholder="`请描述案件情况，或上传相关材料。
例如：我于2025年3月借给王某20万元，约定2025年12月归还……`"
        ></textarea>

        <div class="composer-footer">
          <div class="composer-actions">
            <div ref="uploadControlRef" class="upload-control">
              <button
                type="button"
                class="upload-trigger"
                @click="chooseUpload('material')"
              >
                <svg class="icon sm"><use href="#eb-upload" /></svg
                >加密上传材料
              </button>
              <!-- 下拉菜单暂注释：「上传传统起诉状」功能后端未就绪，点击按钮直接上传案件材料。
                   恢复时：按钮换回 :class="{ open: uploadMenuOpen }" 和
                   @click.stop="uploadMenuOpen = !uploadMenuOpen"，并在按钮文字后补回 chevron 图标
              <div class="upload-menu" :class="{ show: uploadMenuOpen }">
                <button
                  type="button"
                  class="upload-option"
                  @click="chooseUpload('material')"
                >
                  <span class="upload-option-icon"
                    ><svg class="icon sm"><use href="#eb-upload" /></svg
                  ></span>
                  <span class="upload-option-main">
                    <span class="upload-option-title">上传案件材料</span>
                    <span class="upload-option-desc">合同、聊天、转账等</span>
                  </span>
                </button>
                <button
                  type="button"
                  class="upload-option"
                  @click="chooseUpload('complaint')"
                >
                  <span class="upload-option-icon"
                    ><svg class="icon sm"><use href="#eb-file" /></svg
                  ></span>
                  <span class="upload-option-main">
                    <span class="upload-option-title">上传传统起诉状</span>
                    <span class="upload-option-desc">转换为要素式文书</span>
                  </span>
                </button>
              </div>
              -->
            </div>
            <button type="button" class="example-btn" @click="fillExample">
              <svg class="icon sm"><use href="#eb-file" /></svg>示例描述
            </button>
          </div>
          <div class="start-wrap">
            <span class="start-tip">{{
              startReady
                ? "信息已就绪，可开始生成"
                : anyUploading
                ? "材料上传中，请稍候…"
                : "请输入案件情况或上传材料"
            }}</span>
            <button
              type="button"
              class="btn primary"
              :disabled="!startReady"
              @click="startFlow"
            >
              <svg class="icon sm"><use href="#eb-send" /></svg>开始生成
            </button>
          </div>
        </div>

        <input
          ref="materialInputRef"
          type="file"
          multiple
          hidden
          accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
          @change="handleFiles($event, '案件材料')"
        />
        <input
          ref="complaintInputRef"
          type="file"
          hidden
          accept=".doc,.docx,.pdf"
          @change="handleFiles($event, '传统起诉状')"
        />
      </div>

      <!-- 最近文书 -->
      <div class="section-headline">
        <div>
          <h2>最近文书</h2>
          <div class="section-sub">查看近期要素式文书生成任务与生成结果</div>
        </div>
        <button type="button" class="all-record-link" @click="viewAllRecords">
          查看全部记录 <svg class="icon sm"><use href="#eb-chevron" /></svg>
        </button>
      </div>

      <div class="card record-card">
        <div v-if="recordsLoading" class="record-empty">加载中…</div>
        <div v-else-if="!records.length" class="record-empty">暂无最近文书</div>
        <template v-else>
          <div v-for="r in records" :key="r.id" class="record-row">
            <div class="record-main">
              <div class="record-icon">W</div>
              <div class="record-text">
                <div class="record-name">{{ r.name }}</div>
                <div class="record-meta">{{ r.meta }}</div>
              </div>
            </div>
            <div>
              <span v-if="r.status === 'done'" class="status done"
                >生成完成</span
              >
              <span v-else class="status generating"
                ><span class="spinner"></span>生成中</span
              >
            </div>
            <div class="record-time">{{ r.time }}</div>
            <div class="record-actions">
              <button class="btn sm" @click="viewRecord(r)">查看</button>
              <button
                class="btn sm"
                :disabled="r.status !== 'done'"
                @click="downloadRecord(r)"
              >
                下载
              </button>
              <button class="btn sm" @click="deleteRecord(r)">删除</button>
            </div>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script setup>
defineOptions({ name: "ElementBased" });
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import EbIcons from "./components/EbIcons.vue";
import { flowInput, elementConversationId } from "./store";
import {
  getElementExample,
  getRecentIndictmentConversations,
  deleteIndictmentConversation,
  saveIndictmentDoc,
  generateIndictmentFromMaterial,
  EXAMPLE_CASE,
} from "@/api/elementBased";

const router = useRouter();

const caseText = ref("");
// 上传文件：{ name, label, size, raw: File,
//   status: uploading|done|error, percent, material: 上传接口返回实体 }
// material 实体：{ id, fileName, fullText, fileDownloadUrl, sourceText }
const files = ref([]);
// 当前材料来源：text | material | complaint
const sourceType = ref("text");
// 下拉菜单暂注释（随「上传传统起诉状」功能一起恢复）：
// const uploadMenuOpen = ref(false);
const uploadControlRef = ref(null);
const materialInputRef = ref(null);
const complaintInputRef = ref(null);

// 最近文书
const records = ref([]);
const recordsLoading = ref(false);

// 输入来源文案
const INPUT_TYPE_META = {
  natural: "要素式起诉状 · 文字描述生成",
  structured: "要素式起诉状 · 结构化生成",
  material: "要素式起诉状 · 案件材料生成",
  complaint: "传统起诉状转要素式",
};

const pad2 = (n) => String(n).padStart(2, "0");

// 相对时间：今天 / 昨天 / yyyy-MM-dd HH:mm
const formatRecordTime = (raw) => {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  const hm = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startTarget = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const diffDays = Math.round((startToday - startTarget) / 86400000);
  if (diffDays === 0) return `今天 ${hm}`;
  if (diffDays === 1) return `昨天 ${hm}`;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )} ${hm}`;
};

// 实体字段：{ id, sessionId, plaintiff, defendant, claimContent,
//   fileName, fileDownloadUrl, createTime }——没有 status 字段，
// 有下载地址即视为生成完成；文件名去扩展名展示
const normalizeRecentRecord = (raw, index) => {
  const statusRaw = String(
    raw.status ?? raw.Status ?? raw.generateStatus ?? "",
  ).toLowerCase();
  const done =
    statusRaw === "completed" ||
    statusRaw === "done" ||
    statusRaw === "success" ||
    statusRaw === "2" ||
    (!statusRaw && !!raw.fileDownloadUrl);
  const inputType = String(
    raw.inputType ?? raw.InputType ?? raw.sourceType ?? "",
  ).toLowerCase();
  return {
    id: raw.id ?? raw.sessionId ?? raw.SessionId ?? `recent-${index}`,
    aiChatsId: raw.aiChatsId ?? raw.aIChatsId ?? raw.AIChatsId ?? "",
    sessionId: raw.sessionId ?? raw.SessionId ?? raw.id,
    name:
      raw.name ??
      raw.title ??
      (raw.fileName
        ? raw.fileName.replace(/\.(docx?|pdf)$/i, "")
        : undefined) ??
      raw.caseReasonName ??
      "未命名文书",
    meta:
      raw.meta ??
      raw.description ??
      (raw.plaintiff && raw.defendant
        ? `${raw.plaintiff} 诉 ${raw.defendant}`
        : undefined) ??
      INPUT_TYPE_META[inputType] ??
      "要素式起诉状",
    status: done ? "done" : "generating",
    time: formatRecordTime(
      raw.time ?? raw.createTime ?? raw.CreateTime ?? raw.completeTime,
    ),
    fileDownloadUrl: raw.fileDownloadUrl ?? raw.FileDownloadUrl,
  };
};

const fetchRecentRecords = async () => {
  recordsLoading.value = true;
  try {
    const res = await getRecentIndictmentConversations(3);
    // 真实返回为 { data: { list: [...] } }，兼容 data 直接为数组
    const list = res?.data?.list ?? (Array.isArray(res?.data) ? res.data : []);
    records.value = list.map(normalizeRecentRecord);
  } catch (err) {
    console.error("[ElementBased] 获取最近文书失败:", err);
    records.value = [];
  } finally {
    recordsLoading.value = false;
  }
};

// 有文字或已成功上传的材料即可开始生成；材料上传中/全部失败时不可开始
const anyUploading = computed(() =>
  files.value.some((f) => f.status === "uploading"),
);
const startReady = computed(
  () =>
    (!!caseText.value.trim() || files.value.some((f) => f.status === "done")) &&
    !anyUploading.value,
);

// ---------- 上传（下拉菜单暂注释：点击按钮直接上传案件材料） ----------
onMounted(() => {
  fetchRecentRecords();
});

const chooseUpload = (type) => {
  sourceType.value = type;
  const input =
    type === "material" ? materialInputRef.value : complaintInputRef.value;
  input && input.click();
};

// ---------- 材料上传（选择/拖入即上传，GenerateFromMaterial） ----------
// 同一批选择的文件合并为一次上传请求，进度回写到批内每个 chip
const uploadItems = async (items) => {
  try {
    const res = await generateIndictmentFromMaterial(
      items.map((x) => x.raw),
      (percent) => items.forEach((x) => (x.percent = percent)),
    );
    const material = res?.data ?? null;
    items.forEach((x) => {
      x.status = material ? "done" : "error";
      x.material = material;
    });
    if (!material) throw new Error("上传返回为空");
  } catch (err) {
    console.error("[ElementBased] 材料上传失败:", err);
    items.forEach((x) => (x.status = "error"));
    message.error("材料上传失败，请移除后重新上传");
  }
};

// 去重后入列并立即触发上传；返回值取回响应式代理以便上传过程更新状态
const addFiles = (list, label) => {
  const added = [];
  list.forEach((f) => {
    if (files.value.some((x) => x.name === f.name)) return;
    files.value.push({
      name: f.name,
      label,
      size: formatSize(f.size),
      raw: f,
      status: "uploading",
      percent: 0,
    });
    added.push(files.value[files.value.length - 1]);
  });
  if (added.length) uploadItems(added);
};

const handleFiles = (e, label) => {
  addFiles([...(e.target.files || [])], label);
  e.target.value = ""; // 允许再次选择同名文件
};

const fileStateText = (f) =>
  f.status === "uploading"
    ? `加密上传中 ${f.percent || 0}%`
    : f.status === "error"
    ? "上传失败，请移除后重试"
    : "已加密上传";

// ---------- 拖拽上传（整个 composer 卡片为放置区，按「案件材料」处理） ----------
// 与材料入口的 accept 保持一致：Word / PDF / Excel / 图片
const MATERIAL_EXTS = [
  "doc",
  "docx",
  "pdf",
  "xls",
  "xlsx",
  "jpg",
  "jpeg",
  "png",
];
const composerDragOver = ref(false);
// dragenter/leave 会在子元素间连续触发，用计数器避免高亮闪烁
let dragDepth = 0;
// 仅响应文件拖拽（忽略页内文本选区等拖动）
const dragHasFiles = (e) => e.dataTransfer?.types?.includes("Files");
const onDragEnter = (e) => {
  if (!dragHasFiles(e)) return;
  e.preventDefault();
  dragDepth++;
  composerDragOver.value = true;
};
const onDragOver = (e) => {
  if (!dragHasFiles(e)) return;
  e.preventDefault(); // 必须 preventDefault 才允许 drop
};
const onDragLeave = () => {
  dragDepth = Math.max(0, dragDepth - 1);
  if (!dragDepth) composerDragOver.value = false;
};
const onDrop = (e) => {
  e.preventDefault();
  dragDepth = 0;
  composerDragOver.value = false;
  const list = [...(e.dataTransfer.files || [])];
  const invalid = list.filter(
    (f) => !MATERIAL_EXTS.includes(f.name.split(".").pop().toLowerCase()),
  );
  if (invalid.length) {
    message.warning(
      `不支持的文件类型：${invalid
        .map((f) => f.name)
        .join("、")}（支持 Word / PDF / Excel / 图片）`,
    );
  }
  const valid = list.filter((f) => !invalid.includes(f));
  if (valid.length) {
    addFiles(valid, "案件材料");
    sourceType.value = "material";
  }
};

const formatSize = (s) => {
  if (!s) return "1.2 MB";
  if (s < 1024 * 1024) return Math.max(1, Math.round(s / 1024)) + " KB";
  return (s / 1024 / 1024).toFixed(1) + " MB";
};

const isPdf = (name) => name.toLowerCase().endsWith(".pdf");
const removeFile = (i) => files.value.splice(i, 1);

// 示例描述：取 Sample 接口返回 data 第一条的 description 填入输入框
const fillExample = async () => {
  try {
    const res = await getElementExample();
    const description = res?.data?.[0]?.description;
    if (!description) return;
    caseText.value = description;
    sourceType.value = "text";
  } catch (err) {
    console.error("[ElementBased] 获取示例描述失败:", err);
    message.error("示例描述获取失败，请稍后重试");
  }
};

// ---------- 动作 ----------
// 开始生成：写入流程快照后进入生成中页（AI 要素确认）。
// 材料全文（上传接口返回 fullText）拼入流程 text，AI 首条消息即可读到材料内容；
// materials 为上传返回的会话实体（含 id/fileName），结果页用于文书名与真实下载
const startFlow = () => {
  const doneFiles = files.value.filter((f) => f.status === "done");
  // 同批上传的文件共享同一实体，按 id 去重
  const materialList = [
    ...new Map(
      doneFiles
        .map((f) => f.material)
        .filter(Boolean)
        .map((m) => [m.id, m]),
    ).values(),
  ];
  const fullText = materialList
    .map((m) => m.fullText || m.sourceText || "")
    .filter(Boolean)
    .join("\n\n");

  let text = caseText.value.trim();
  if (fullText) {
    if (text) {
      text = `${text}\n\n【上传材料】\n${fullText}`;
    } else if (sourceType.value === "complaint") {
      text = `已上传传统起诉状，请识别以下内容并转换为要素式起诉状。\n\n${fullText}`;
    } else {
      text = fullText;
    }
  } else if (!text && doneFiles.length) {
    // 接口未返回材料全文时按来源生成默认描述（与原型一致）
    text =
      sourceType.value === "complaint"
        ? "已上传传统起诉状，请识别现有内容并转换为要素式起诉状。"
        : "已上传案件相关材料，请根据材料识别案件要素并生成要素式起诉状。";
  }
  if (!text) text = EXAMPLE_CASE;

  // 上传返回的会话 id：写入 store 并随路由 query 带到生成页（AIChats/Add 关联用）
  const conversationId = materialList[0]?.id ?? null;
  elementConversationId.value = conversationId;

  flowInput.value = {
    text,
    files: doneFiles.map((f) => ({
      name: f.name,
      label: f.label,
      size: f.size,
    })),
    sourceType: doneFiles.length ? sourceType.value : "text",
    materials: materialList,
  };
  router.push(
    conversationId
      ? {
          path: "/home/ElementBased/generateResults",
          query: { id: conversationId },
        }
      : "/home/ElementBased/generateResults",
  );
};

// 全部记录：进入要素式文书生成记录列表页
const viewAllRecords = () => {
  router.push("/home/ElementBased/generateList");
};

// 查看记录：进入聊天记录页（可续聊），
// id 为记录 id（Detail / 下载用），chatId 为 aiChatsId（查历史 / 续聊用）
const viewRecord = (r) => {
  router.push({
    path: "/home/ElementBased/generateResults",
    query: {
      view: "chat",
      id: r?.id ?? "",
      chatId: r?.aiChatsId || "",
      name: r?.name || "",
    },
  });
};
const downloadRecord = async (item) => {
  if (!item?.id || item.status !== "done") return;
  try {
    await saveIndictmentDoc(item);
    message.success("文件下载成功");
  } catch (err) {
    console.error("[ElementBased] 下载文书失败:", err);
    message.error("文书下载失败，请稍后重试");
  }
};

const deletingId = ref(null);
const deleteRecord = (item) => {
  if (!item?.id || deletingId.value != null) return;
  Modal.confirm({
    title: "删除文书记录",
    content: `确定删除「${item.name || "未命名文书"}」吗？删除后不可恢复。`,
    okText: "确认",
    cancelText: "取消",
    onOk: async () => {
      deletingId.value = item.id;
      try {
        await deleteIndictmentConversation(item.id);
        message.success("已删除");
        await fetchRecentRecords();
      } catch (err) {
        console.error("[ElementBased] 删除文书失败:", err);
        message.error("删除失败");
      } finally {
        deletingId.value = null;
      }
    },
  });
};
</script>

<style scoped>
/* 变量挂在页面根节点（scoped 下 :root 不可用） */
.element-based-home {
  --brand: #1768ff;
  --brand-hover: #0e59db;
  --brand-soft: #eef4ff;
  --ink: #17243a;
  --line: #e5ebf3;
  --line-2: #f0f3f7;
  --success-soft: #eaf8f2;
  --shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
  --radius: 16px;
  width: 100%;
  margin-top: 24px;
  color: var(--ink);
}

/* ---------- 通用 ---------- */
button {
  cursor: pointer;
}
.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}
.card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.btn:hover {
  background: #f8fbff;
  border-color: #c9d6e8;
}
.btn.primary {
  background: var(--brand);
  border-color: var(--brand);
  color: #fff;
}
.btn.primary:hover {
  background: var(--brand-hover);
}
.btn:disabled {
  background: #f4f6f9 !important;
  border-color: #e1e6ee !important;
  color: #a1adbd !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}
.btn.primary:disabled {
  background: #a9c5f8 !important;
  border-color: #a9c5f8 !important;
  color: #fff !important;
}
.btn.sm {
  height: 32px;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 12px;
  gap: 6px;
}
.icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: #60718a;
  display: grid;
  place-items: center;
}
.icon-btn:hover {
  background: var(--brand-soft);
  color: var(--brand);
}

/* ---------- 首页 ---------- */
.page-wrap {
  max-width: 1080px;
  margin: 0 auto;
  padding-bottom: 24px;
}
.page-title {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
}
.page-subtitle {
  margin-top: 10px;
  color: #65758e;
  font-size: 15px;
  line-height: 1.7;
}

.composer {
  margin-top: 22px;
  min-height: 306px;
  padding: 18px 24px 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: border-color 0.14s, box-shadow 0.14s, background 0.14s;
}
.composer.dragover {
  border-color: var(--brand);
  background: #f7faff;
  box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.08), var(--shadow);
}
.composer-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.composer-textarea {
  width: 100%;
  flex: 1 1 auto;
  min-height: 176px;
  resize: none;
  border: 0;
  outline: 0;
  padding: 2px 2px 12px;
  color: #263b58;
  font-size: 15px;
  line-height: 1.75;
  background: transparent;
  overflow-y: auto;
}
.composer-textarea::placeholder {
  color: #8092af;
}
.composer-footer {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 8px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
}
.composer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
}
.start-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-height: 40px;
}
.start-tip {
  font-size: 11px;
  color: #8a97aa;
  white-space: nowrap;
}

/* 上传菜单 */
.upload-control {
  position: relative;
}
.upload-trigger {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.upload-trigger:hover,
.upload-trigger.open {
  background: #f8fbff;
  border-color: #c9d6e8;
}
.upload-trigger .trigger-caret {
  margin-left: 2px;
}
.upload-menu {
  position: absolute;
  left: 0;
  bottom: 48px;
  width: 240px;
  padding: 7px;
  background: #fff;
  border: 1px solid #e2e7ee;
  border-radius: 14px;
  box-shadow: 0 14px 38px rgba(28, 45, 72, 0.16);
  z-index: 80;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px) scale(0.985);
  transform-origin: left bottom;
  transition: opacity 0.14s, transform 0.14s, visibility 0.14s;
}
.upload-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}
.upload-option {
  width: 100%;
  border: 0;
  border-radius: 9px;
  background: #fff;
  padding: 10px 11px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  color: #24364f;
}
.upload-option:hover {
  background: #f5f7fa;
}
.upload-option-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--brand-soft);
  color: var(--brand);
  display: grid;
  place-items: center;
  flex: none;
  margin-top: 1px;
}
.upload-option-main {
  flex: 1;
  min-width: 0;
}
.upload-option-title {
  display: block;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}
.upload-option-desc {
  display: block;
  font-size: 11px;
  color: #8491a5;
  line-height: 1.4;
  margin-top: 4px;
}
.example-btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.example-btn:hover {
  background: #f8fbff;
  border-color: #c9d6e8;
}

/* 文件 chip */
.file-chip {
  height: 58px;
  min-width: 250px;
  max-width: 350px;
  border: 1px solid #dfe7f2;
  border-radius: 11px;
  background: #fbfdff;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 9px 8px 12px;
}
.file-icon {
  width: 34px;
  height: 38px;
  border: 1px solid #dfe5ee;
  border-radius: 6px;
  background: #f3f6fa;
  color: var(--brand);
  font-size: 10px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex: none;
}
.file-main {
  min-width: 0;
  flex: 1;
}
.file-name {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #31435e;
}
.file-meta {
  font-size: 11px;
  color: #8996a8;
  margin-top: 4px;
}
.file-badge {
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--brand-soft);
  color: var(--brand);
  font-size: 10px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}
.file-chip .icon-btn {
  width: 28px;
  height: 28px;
  flex: none;
}

/* 最近文书 */
.section-headline {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin: 38px 0 16px;
}
.section-headline h2 {
  font-size: 19px;
  margin: 0;
  font-weight: 800;
}
.section-sub {
  font-size: 12px;
  color: #8492a6;
  margin-top: 6px;
}
.all-record-link {
  height: 32px;
  border: 0;
  background: transparent;
  color: var(--brand);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}
.all-record-link:hover {
  background: var(--brand-soft);
}
.record-card {
  overflow: hidden;
}
.record-empty {
  padding: 36px 18px;
  text-align: center;
  color: #8492a6;
  font-size: 13px;
}
.record-row {
  min-height: 88px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 145px 250px;
  gap: 16px;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line-2);
}
.record-row:last-child {
  border-bottom: 0;
}
.record-row:hover {
  background: #fbfdff;
}
.record-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 13px;
}
.record-icon {
  width: 38px;
  height: 42px;
  border: 1px solid #e2e8f1;
  border-radius: 7px;
  background: #f6f8fb;
  color: var(--brand);
  font-size: 10px;
  font-weight: 900;
  display: grid;
  place-items: center;
  flex: none;
}
.record-text {
  min-width: 0;
}
.record-name {
  font-size: 14px;
  font-weight: 760;
  color: #273a55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.record-meta {
  font-size: 12px;
  color: #7f8da1;
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status {
  height: 25px;
  width: max-content;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 750;
}
.status.done {
  background: var(--success-soft);
  color: #16825e;
}
.status.generating {
  background: var(--brand-soft);
  color: var(--brand);
}
.record-time {
  font-size: 12px;
  color: #74839a;
  white-space: nowrap;
}
.record-actions {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 7px;
}
.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid #cfe0ff;
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 6px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
